import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private http: HttpClient,
    private storage: StorageService
  ) {}

  /**
   * Used to point requests to either the slp-content-api or learning-service.
   *
   * 1. API calls to `slp-content-api` use relative urls.
   *    Example: this.http.get('/users') → 'https://api.slp.com/users'
   *
   * 2. API calls to `learning-service` use absolute urls.
   *    Example: this.http.get(environment.learningServiceBaseUrl + '/health/live') → 'https://learning-service.com/health/live'
   */
  private resolveUrl(request: string): string {
    return /^https?:\/\//i.test(request) ? request : environment.apiUrl + request;
  }

  put(request: string, obj: any = {}): Observable<any> {
    var options = {
      headers: this.getHeaders(),
    };

    return this.http.put(this.resolveUrl(request), JSON.stringify(obj), options);
  }

  post(request: string, obj: any = {}): Observable<any> {
    var options = {
      headers: this.getHeaders(),
    };

    return this.http.post(this.resolveUrl(request), JSON.stringify(obj), options);
  }

  /**
   * Convert params object to HttpParams, properly handling arrays
   */
  private toHttpParams(params: any): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach(key => {
      const value = params[key];
      if (Array.isArray(value)) {
        // For arrays, append each value separately
        value.forEach(item => {
          httpParams = httpParams.append(key, item);
        });
      } else if (value !== null && value !== undefined) {
        httpParams = httpParams.append(key, value);
      }
    });

    return httpParams;
  }

  get(request: string, params?: any): Observable<any> {
    var options: any = {
      headers: this.getHeaders(),
    };

    if (params) {
      options.params = this.toHttpParams(params);
    }

    return this.http.get(this.resolveUrl(request), options);
  }

  delete(request: string, params?: any): Observable<any> {
    var options: any = {
      headers: this.getHeaders(),
    };

    if (params) {
      options.params = this.toHttpParams(params);
    }

    return this.http.delete(this.resolveUrl(request), options);
  }

  uploadMedia(file: File, type: 'video' | 'image' | 'file', folder?: string): Observable<any> {
    var preset = '';
    if (type === 'video') {
      preset = 'cms-video-preset';
    } else if (type === 'image') {
      preset = 'cms-image-preset';
    } else {
      preset = 'cms-file-preset';
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', preset);

    return this.http.post(environment.cloudinaryUrl, formData);
  }

  getHeaders(): HttpHeaders {
    var headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const token = this.storage.getToken();
    if (token) {
      headers = headers.append('Authorization', token);
    }

    return headers;
  }
}
